import { Page, EntityName, PageSkeleton } from '@/components';
import { useApi } from '@/api';
import { useParams } from 'react-router';
import {
  Flex,
  KpiCard,
  CryptoIcon,
  PercentageValue,
  Heading,
  KpiSkeleton,
  ChartSkeleton
} from '@blockanalitica/ui';
import CollateralsTable from './CollateralsTable';
import { useNetwork } from '@/hooks';
import MarketActivity from './MarketActivity';
import HistoricGraphs from './HistoricGraphs';
import HistoricAprGraphs from './HistoricAprGraphs';
import CollateralAssetsTabs from './CollateralAssetsTabs';
import TopSuppliers from './TopSuppliers';
import TopBorrowers from './TopBorrowers';

type MarketResponse = {
  address: string;
  name: string;
  underlying_symbol: string;
  underlying_address: string;
  total_supply: string | null;
  total_supply_usd: string | null;
  total_borrow: string | null;
  total_borrow_usd: string | null;
  tvl: string | null;
  tvl_usd: string | null;
  utilization: string | null;
  supply_rate: string | null;
  supply_reward_rate: string | null;
  borrow_rate: string | null;
  borrow_reward_rate: string | null;
  underlying_price: string | null;
  earn_apr: string | null;
  borrow_apr: string | null;
  total_supply_change: string | null;
  total_supply_change_percentage: string | null;
  total_supply_usd_change: string | null;
  total_supply_usd_change_percentage: string | null;
  total_borrow_change: string | null;
  total_borrow_change_percentage: string | null;
  total_borrow_usd_change: string | null;
  total_borrow_usd_change_percentage: string | null;
  earn_apr_change: string | null;
  earn_apr_change_percentage: string | null;
  borrow_apr_change: string | null;
  borrow_apr_change_percentage: string | null;
  utilization_change: string | null;
  utilization_change_percentage: string | null;
  tvl_change: string | null;
  tvl_change_percentage: string | null;
  tvl_usd_change: string | null;
  tvl_usd_change_percentage: string | null;
  underlying_price_change: string | null;
  underlying_price_change_percentage: string | null;
};

export default function MarketPage() {
  const network = useNetwork() as string;
  const { marketAddress } = useParams() as { marketAddress: string };

  const [data, error] = useApi<MarketResponse>({
    url: `/markets/${marketAddress}/`
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return (
      <PageSkeleton>
        <Flex gap={['s', 'xs']} flexDirection={['column', 'row']}>
          <KpiSkeleton />
          <KpiSkeleton />
          <KpiSkeleton />
        </Flex>
        <ChartSkeleton />
        <Flex gap={['s', 'xs']} flexDirection={['column', 'row']}>
          <KpiSkeleton />
          <KpiSkeleton />
        </Flex>
      </PageSkeleton>
    );
  }
  return (
    <Page
      title={
        <EntityName
          entitySize="4"
          symbol1={
            <CryptoIcon
              name={data.underlying_symbol}
              address={data.underlying_address}
              network={network}
              size="4"
            />
          }>
          <Heading>{data.name}</Heading>
        </EntityName>
      }>
      <Flex gap={['s', 'xs']} flexDirection={['column', 'row']}>
        <KpiCard
          title="Total Earning"
          data={data}
          field="total_supply"
          options={{
            valueOptions: {
              currencyValueOptions: {
                currencySize: '3',
                currency: {
                  name: data.underlying_symbol,
                  address: data.underlying_address,
                  network: network
                },
                notation: 'compact'
              }
            },
            valueTrendOptions: {
              currencyValueOptions: {
                trendIconSize: '0'
              }
            }
          }}
        />
        <KpiCard
          title="Total Borrowing"
          data={data}
          field="total_borrow"
          options={{
            valueOptions: {
              currencyValueOptions: {
                currencySize: '3',
                currency: {
                  name: data.underlying_symbol,
                  address: data.underlying_address,
                  network: network
                },
                notation: 'compact'
              }
            },
            valueTrendOptions: {
              currencyValueOptions: {
                trendIconSize: '0'
              }
            }
          }}
        />
        <KpiCard
          title="Total Value Locked"
          data={data}
          field="tvl"
          options={{
            valueOptions: {
              currencyValueOptions: {
                currencySize: '3',
                currency: {
                  name: data.underlying_symbol,
                  address: data.underlying_address,
                  network: network
                },
                notation: 'compact'
              }
            },
            valueTrendOptions: {
              currencyValueOptions: {
                trendIconSize: '0'
              }
            }
          }}
        />
      </Flex>
      <HistoricGraphs
        underlyingAddress={data.underlying_address}
        marketSymbol={data.underlying_symbol}
        network={network}
        marketAddress={marketAddress}
      />
      <Flex gap={['s', 'xs']} flexDirection={['column', 'row']}>
        <Flex flexDirection="column" gap={['s', 'xs']} flexBasis="33%">
          <KpiCard
            title="Earn Apr"
            data={data}
            field="supply_rate"
            options={{
              percentage: true,
              notation: 'compact',
              variants: {
                valuePercentageValue: 'dynamic'
              }
            }}
            footer={
              <Flex gap="3xs" alignItems="center">
                <CryptoIcon
                  name={data.underlying_symbol}
                  address={data.underlying_address}
                  network={network}
                  size="-1"
                />
                <PercentageValue value={data.supply_rate} />
              </Flex>
            }
          />
          <KpiCard
            title="Borrow Apr"
            data={data}
            field="borrow_rate"
            options={{
              percentage: true,
              notation: 'compact',
              variants: {
                valuePercentageValue: 'dynamic'
              }
            }}
            footer={
              <Flex gap="3xs" alignItems="center">
                <CryptoIcon
                  name={data.underlying_symbol}
                  address={data.underlying_address}
                  network={network}
                  size="-1"
                />
                <PercentageValue value={data.borrow_rate} />
              </Flex>
            }
          />
          <KpiCard
            title="Utilization"
            data={data}
            field="utilization"
            options={{
              percentage: true,
              notation: 'compact',
              variants: {
                valuePercentageValue: 'dynamic'
              }
            }}
          />
          <KpiCard
            title="Oracle Price"
            data={data}
            field="underlying_price"
            options={{
              valueOptions: {
                currencyValueOptions: {
                  formatCurrencyOptions: {
                    maximumFractionDigits:
                      Number(data.underlying_price) < 1.1 ? 5 : 2
                  }
                }
              },
              variants: {
                valuePercentageValue: 'dynamic'
              }
            }}
          />
        </Flex>
        <HistoricAprGraphs marketAddress={marketAddress} flexBasis="67%" />
      </Flex>
      <Heading size="2">Collateral Assets</Heading>
      <CollateralAssetsTabs marketAddress={marketAddress} />
      <CollateralsTable marketAddress={marketAddress} />
      <Flex gap={['s', 'xs']} flexDirection={['column', 'row']}>
        <TopSuppliers marketAddress={marketAddress} network={network} />
        <TopBorrowers marketAddress={marketAddress} network={network} />
      </Flex>
      <MarketActivity marketAddress={marketAddress} />
    </Page>
  );
}
